import React, { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { CloudDownload, Trash2, RefreshCw, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { OfflineSetupDialog } from "./OfflineSetupDialog";
import { OfflineManagePanel } from "./OfflineFullDialog";
import { getOfflineSettings, saveOfflineSettings, syncNow, clearOfflineData, usage, fmtBytes, subscribeOffline, state as syncState } from "../lib/offlineSync";

/** Mais → Configurações → Modo offline neste aparelho */
export const OfflineSettingsCard = ({ user, t }) => {
  const [settings, setSettings] = useState(() => getOfflineSettings(user.id) || { enabled: false });
  const [setupOpen, setSetupOpen] = useState(() => new URLSearchParams(window.location.search).get("setup") === "1");
  const [manageOpen, setManageOpen] = useState(false);
  const [use, setUse] = useState(null);
  const [sync, setSync] = useState({ ...syncState });
  const refresh = () => usage().then(setUse).catch(() => {});
  useEffect(() => { refresh(); return subscribeOffline((type, d) => { if (type === "state") { setSync({ ...d }); if (!d.syncing) refresh(); } if (type === "settings") setSettings(d); if (type === "media") refresh(); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (on) => {
    if (on) { setSetupOpen(true); return; }
    saveOfflineSettings(user.id, { enabled: false });
    await clearOfflineData(user.id);
    toast.info(t("offline_disabled_toast"));
    refresh();
  };

  return (
    <Card className="p-4 space-y-3" data-testid="offline-settings-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CloudDownload className="h-5 w-5 text-primary" />
          <div>
            <div className="text-sm font-medium">{t("offline_mode")}</div>
            <div className="text-xs text-muted-foreground">{settings.enabled ? t("offline_mode_on_hint") : t("offline_mode_off_hint")}</div>
          </div>
        </div>
        <Switch checked={!!settings.enabled} onCheckedChange={toggle} data-testid="offline-mode-switch" />
      </div>
      {settings.enabled && (
        <>
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
            <span>{t("offline_history")}: <b>{t(`offline_history_${settings.history || "90d"}`)}</b></span>
            <span>{t("offline_photos")}: <b>{t(`offline_photos_${settings.photos || "thumb"}`)}</b></span>
          </div>
          {use && (
            <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="offline-usage">
              <HardDrive className="h-3.5 w-3.5" />
              {use.conversations} {t("conversations").toLowerCase()} · {use.messages} {t("msgs").toLowerCase()} · {use.mediaCount} {t("offline_media_files")} ({fmtBytes(use.mediaBytes)})
              {use.quota?.quota ? ` · ${t("offline_device_free")}: ${fmtBytes(use.quota.quota - use.quota.usage)}` : ""}
            </div>
          )}
          {sync.syncing && (
            <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${sync.progress}%` }} /></div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="offline-sync-now" disabled={sync.syncing} onClick={() => syncNow(user, { full: true })}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${sync.syncing ? "animate-spin" : ""}`} />{sync.syncing ? `${sync.progress}%` : t("offline_sync_now")}
            </Button>
            <Button size="sm" variant="outline" data-testid="offline-change-options" onClick={() => setSetupOpen(true)}>{t("offline_change_options")}</Button>
            <Button size="sm" variant="outline" data-testid="offline-manage" onClick={() => setManageOpen(true)}><Trash2 className="h-3.5 w-3.5 mr-1.5" />{t("offline_manage")}</Button>
          </div>
        </>
      )}
      <OfflineSetupDialog open={setupOpen} onClose={() => { setSetupOpen(false); setSettings(getOfflineSettings(user.id) || { enabled: false }); refresh(); }} user={user} t={t} />
      <OfflineManagePanel open={manageOpen} onClose={() => { setManageOpen(false); refresh(); }} user={user} t={t} />
    </Card>
  );
};
